import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import NotesIcon from '@mui/icons-material/Notes';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import { useNavigate } from 'react-router-dom';

import {
  assetTextToMarkdown,
  copyMarkdownToClipboard,
  downloadMarkdown,
} from '../../labs/WriterLab/utils/exportToMarkdown';

import type { AssetRelationshipDto, AssetSummary, LifecycleState } from '../../domain';
import { LIFECYCLE_STATES } from '../../domain';
import {
  useAddAssetsToCollection,
  useAssetFamily,
  useCollections,
  useCreateCollection,
  useSetLifecycleState,
  useStudio,
} from '../../queries';
import { useSourceHandoffStore } from '../../store/sourceHandoffStore';
import { useCaptionHandoffStore } from '../../store/captionHandoffStore';
import { useAnimateHandoffStore } from '../../store/animateHandoffStore';
import { assetFileUrl, formatDimensions, formatFileSize } from './assetUtils';
import { lifecycleMeta } from './lifecycleUtils';
import { studioMetadataRows } from './studioMetadata';
import {
  documentText,
  extensionForAsset,
  filenameForAsset,
  findAdjacentAsset,
  formatTimestamp,
  isDocumentAsset,
} from './previewHelpers';

interface Props {
  asset: AssetSummary | null;
  /** Full visible asset list — used to navigate prev/next. */
  assets: AssetSummary[];
  /** Favorited asset ids (for the heart state). */
  favoritedIds?: Set<string>;
  onToggleFavorite?: (asset: AssetSummary) => void;
  onClose: () => void;
  onNavigate: (asset: AssetSummary) => void;
}

type Toast = { msg: string; severity: 'success' | 'error' };

/**
 * Full-screen-ish preview of a single asset. Image on the left, metadata +
 * actions on the right. Keyboard: Escape closes, ArrowLeft / ArrowRight
 * navigate adjacent assets. The sidebar surfaces Path C affordances:
 * favorite (Slice 8), lifecycle chip + promote (Slice 9), variation lineage
 * (Slice 9), and add-to-collection (Slice 7).
 */
export const AssetPreviewModal: React.FC<Props> = ({
  asset,
  assets,
  favoritedIds,
  onToggleFavorite,
  onClose,
  onNavigate,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const setHandoff = useSourceHandoffStore(s => s.setHandoff);
  const setCaptionHandoff = useCaptionHandoffStore(s => s.setHandoff);
  const setAnimateHandoff = useAnimateHandoffStore(s => s.setHandoff);
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const [toast, setToast] = React.useState<Toast | null>(null);
  const [imgError, setImgError] = React.useState(false);

  const setLifecycle = useSetLifecycleState();
  const family = useAssetFamily(asset?.assetId);
  // Studio-typed metadata (Path C Slice 6, read-only). The gallery is outside
  // the ImageLab route, so resolve the studio by the asset's own studioId;
  // useStudio no-ops when that's absent.
  const studio = useStudio(asset?.studioId).data;
  const metadataSchema = studio?.metadataSchema;
  const studioRows = React.useMemo(() => {
    if (!asset || !metadataSchema) return [];
    const block = (asset.metadata as Record<string, unknown> | undefined)?.[
      metadataSchema.metadataKey
    ] as Record<string, unknown> | undefined;
    return studioMetadataRows(metadataSchema, block);
  }, [asset, metadataSchema]);

  React.useEffect(() => {
    setImgError(false);
  }, [asset?.assetId]);

  const prev = asset ? findAdjacentAsset(assets, asset.assetId, 'prev') : null;
  const next = asset ? findAdjacentAsset(assets, asset.assetId, 'next') : null;

  React.useEffect(() => {
    if (!asset) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prev) {
        e.preventDefault();
        onNavigate(prev);
      } else if (e.key === 'ArrowRight' && next) {
        e.preventDefault();
        onNavigate(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [asset, prev, next, onNavigate]);

  const handleCopyUrl = async () => {
    if (!asset) return;
    try {
      await navigator.clipboard.writeText(asset.url);
      setToast({ msg: 'URL copied to clipboard', severity: 'success' });
    } catch {
      setToast({ msg: 'Failed to copy URL', severity: 'error' });
    }
  };

  const handleDownload = async () => {
    if (!asset) return;
    try {
      const res = await fetch(asset.url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filenameForAsset(asset);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const a = document.createElement('a');
      a.href = asset.url;
      a.download = filenameForAsset(asset);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setToast({
        msg: err instanceof Error ? err.message : 'Download fell back to direct link',
        severity: 'error',
      });
    }
  };

  const handleUseAsSource = () => {
    if (!asset) return;
    setHandoff(asset);
    const studio = asset.studioId?.trim() || 'generic';
    navigate(`/labs/image-lab/${studio}`);
    onClose();
  };

  // A-F7: route a still into Animation Lab's Motion studio, pre-filling the
  // Image→Video panel with this asset (the still becomes the canvas poster).
  const handleAnimate = () => {
    if (!asset) return;
    setAnimateHandoff({
      sourceAssetId: asset.assetId,
      sourceSignedUrl: asset.signedUrl ?? asset.url,
    });
    navigate('/labs/animation-lab/motion');
    onClose();
  };

  // D-7: route an image asset into Influencer Studio with the image attached as
  // the caption's reference.
  const handleWriteCaption = () => {
    if (!asset) return;
    setCaptionHandoff(asset);
    navigate('/labs/writer-lab/influencer');
    onClose();
  };

  // Gap 2 — document export to markdown. Persisted v3.0 documents are paragraph-only,
  // so the document text doubles as a valid markdown rendering. Rich-structure round-trip
  // arrives when the BE persists full Lexical EditorState JSON (v3.0.1).
  const handleCopyMarkdown = async () => {
    if (!asset) return;
    const md = assetTextToMarkdown(documentText(asset));
    if (!md) {
      setToast({ msg: 'This document has no text content to copy.', severity: 'error' });
      return;
    }
    try {
      await copyMarkdownToClipboard(md);
      setToast({ msg: 'Copied as Markdown', severity: 'success' });
    } catch (err) {
      setToast({
        msg: err instanceof Error ? err.message : 'Copy failed.',
        severity: 'error',
      });
    }
  };

  const handleDownloadMarkdown = () => {
    if (!asset) return;
    const md = assetTextToMarkdown(documentText(asset));
    if (!md) {
      setToast({ msg: 'This document has no text content to download.', severity: 'error' });
      return;
    }
    const titleHint =
      (typeof asset.metadata?.title === 'string' && asset.metadata.title) ||
      asset.promptUsed ||
      asset.assetId;
    downloadMarkdown(md, titleHint);
    setToast({ msg: 'Downloaded as Markdown', severity: 'success' });
  };

  const handleLifecycleChange = (state: LifecycleState) => {
    if (!asset) return;
    setLifecycle.mutate(
      { assetId: asset.assetId, state },
      {
        onSuccess: () => setToast({ msg: `Lifecycle set to ${state}`, severity: 'success' }),
        onError: () => setToast({ msg: 'Failed to update lifecycle', severity: 'error' }),
      },
    );
  };

  const isFavorited = asset ? favoritedIds?.has(asset.assetId) ?? false : false;
  const currentLifecycle = (asset?.lifecycleState as LifecycleState) ?? 'Draft';
  const isDocument = asset ? isDocumentAsset(asset) : false;

  // Navigate the preview to a related (lineage) asset we only know by id.
  const navigateToRelated = (assetId: string) => {
    if (!asset) return;
    const existing = assets.find(a => a.assetId === assetId);
    if (existing) {
      onNavigate(existing);
      return;
    }
    onNavigate({
      assetId,
      url: assetFileUrl(assetId),
      kind: 'image',
      sizeBytes: 0,
      appAgentType: asset.appAgentType,
      createdAt: '',
    });
  };

  return (
    <>
      <Dialog
        open={asset !== null}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={!isWide}
        PaperProps={{ sx: { height: { md: '85vh' } } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', minHeight: 0 }}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close preview"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {asset && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ flex: 1, minHeight: 0, width: '100%' }}
            >
              {/* Media pane — image, or read-only document text */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                  // A media stage stays dark in both color modes so an image
                  // reads against it and the white-on-stage chrome (nav
                  // arrows, "Preview unavailable") keeps its contrast. The
                  // light arm used to be `#0b0e16` — a cold near-black that
                  // only ever looked deliberate next to a dark shell; it
                  // lands as a slab in a light-default theme.
                  bgcolor: isDocument ? 'background.default' : 'common.black',
                  display: 'flex',
                  alignItems: isDocument ? 'stretch' : 'center',
                  justifyContent: 'center',
                  overflow: isDocument ? 'auto' : 'hidden',
                  p: isDocument ? 4 : 2,
                }}
              >
                {isDocument ? (
                  <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto' }} aria-label="Document content">
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                    >
                      {documentText(asset) || (
                        <Typography component="span" color="text.secondary">
                          This document has no preview text.
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                ) : imgError ? (
                  <Stack alignItems="center" spacing={1} sx={{ color: 'common.white' }}>
                    <ImageOutlinedIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography variant="caption">Preview unavailable</Typography>
                  </Stack>
                ) : (
                  <Box
                    component="img"
                    // Prefer the HMAC-signed companion. The unsigned URL 401s
                    // for native `<img>` loads because the browser can't
                    // attach `x-user-id`. See
                    // qa/bugs/2026-05-25-asset-files-401-canvas-render.md.
                    src={asset.signedUrl ?? asset.url}
                    alt={asset.promptUsed ?? asset.assetId}
                    onError={() => setImgError(true)}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                    }}
                  />
                )}

                {prev && (
                  <Tooltip title="Previous (←)">
                    <IconButton
                      onClick={() => onNavigate(prev)}
                      aria-label="Previous asset"
                      sx={navButtonSx('left')}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {next && (
                  <Tooltip title="Next (→)">
                    <IconButton
                      onClick={() => onNavigate(next)}
                      aria-label="Next asset"
                      sx={navButtonSx('right')}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Metadata sidebar */}
              <Box
                sx={{
                  width: { xs: '100%', md: 320 },
                  flexShrink: 0,
                  borderLeft: { md: theme => `1px solid ${theme.palette.divider}` },
                  borderTop: { xs: theme => `1px solid ${theme.palette.divider}`, md: 'none' },
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                  {/* Lifecycle chip + favorite heart */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5, pr: 4 }}
                  >
                    <Chip
                      label={lifecycleMeta(currentLifecycle).label}
                      color={lifecycleMeta(currentLifecycle).color}
                      size="small"
                      variant="outlined"
                    />
                    {onToggleFavorite && (
                      <Tooltip title={isFavorited ? 'Unfavorite' : 'Favorite'}>
                        <IconButton
                          size="small"
                          aria-label={isFavorited ? 'Unfavorite asset' : 'Favorite asset'}
                          onClick={() => onToggleFavorite(asset)}
                          sx={{ color: isFavorited ? 'error.main' : 'text.secondary' }}
                        >
                          {isFavorited ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Typography variant="overline" color="text.secondary">
                    Prompt
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {asset.promptUsed?.trim() || '—'}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <MetaRow label="Type" value={asset.subType ?? asset.kind} />
                  <MetaRow
                    label="Dimensions"
                    value={formatDimensions(asset.width, asset.height) || '—'}
                  />
                  <MetaRow label="Size" value={formatFileSize(asset.sizeBytes)} />
                  <MetaRow label="Format" value={extensionForAsset(asset).toUpperCase()} />
                  <MetaRow label="Model" value={asset.model ?? '—'} />
                  <MetaRow label="Provider" value={asset.provider ?? '—'} />
                  <MetaRow label="Created" value={formatTimestamp(asset.createdAt)} />
                  {asset.sessionName && <MetaRow label="Session" value={asset.sessionName} />}

                  {/* Lifecycle promote */}
                  <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="lifecycle-label">Lifecycle</InputLabel>
                    <Select
                      labelId="lifecycle-label"
                      label="Lifecycle"
                      value={currentLifecycle}
                      disabled={setLifecycle.isPending}
                      onChange={e => handleLifecycleChange(e.target.value as LifecycleState)}
                    >
                      {LIFECYCLE_STATES.map(s => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Studio-typed metadata (Path C Slice 6, read-only) —
                      schema-driven, so it covers any studio with a metadataSchema. */}
                  {studioRows.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="overline" color="text.secondary">
                        Studio details
                      </Typography>
                      {studioRows.map(row => (
                        <MetaRow key={row.label} label={row.label} value={row.value} />
                      ))}
                    </>
                  )}

                  {/* Variation lineage */}
                  <LineageSection
                    parents={family.data?.parents ?? []}
                    childrenRels={family.data?.children ?? []}
                    onOpenRelated={navigateToRelated}
                  />
                </Box>

                <Stack
                  spacing={1}
                  sx={{
                    p: 2,
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {!isDocument && (
                    <Button
                      variant="contained"
                      startIcon={<OpenInNewIcon />}
                      onClick={handleUseAsSource}
                      fullWidth
                    >
                      Use as source
                    </Button>
                  )}
                  {asset.kind === 'image' && (
                    <Button
                      variant="outlined"
                      startIcon={<MovieFilterOutlinedIcon />}
                      onClick={handleAnimate}
                      fullWidth
                    >
                      Animate this still
                    </Button>
                  )}
                  {asset.kind === 'image' && (
                    <Button
                      variant="outlined"
                      startIcon={<EditNoteIcon />}
                      onClick={handleWriteCaption}
                      fullWidth
                    >
                      Write caption
                    </Button>
                  )}
                  {/* Gap 2 — markdown export for document assets. */}
                  {isDocument && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyMarkdown}
                        fullWidth
                      >
                        Copy as Markdown
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<NotesIcon />}
                        onClick={handleDownloadMarkdown}
                        fullWidth
                      >
                        Download .md
                      </Button>
                    </>
                  )}
                  <AddToCollectionButton
                    asset={asset}
                    onToast={setToast}
                  />
                  {!isDocument && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      fullWidth
                    >
                      Download
                    </Button>
                  )}
                  {!isDocument && (
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyUrl}
                      fullWidth
                    >
                      Copy URL
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

const MetaRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

/** Humanize a relationshipType ("InpaintOf" -> "Inpaint of"). */
function humanizeRelationship(type: string): string {
  const spaced = type.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

const LineageSection: React.FC<{
  parents: AssetRelationshipDto[];
  childrenRels: AssetRelationshipDto[];
  onOpenRelated: (assetId: string) => void;
}> = ({ parents, childrenRels, onOpenRelated }) => {
  if (parents.length === 0 && childrenRels.length === 0) return null;
  return (
    <>
      <Divider sx={{ my: 1.5 }} />
      {parents.length > 0 && (
        <LineageRow
          title="Derived from"
          items={parents.map(p => ({ id: p.parentAssetId, type: p.relationshipType }))}
          onOpen={onOpenRelated}
        />
      )}
      {childrenRels.length > 0 && (
        <LineageRow
          title="Variations / derivatives"
          items={childrenRels.map(c => ({ id: c.childAssetId, type: c.relationshipType }))}
          onOpen={onOpenRelated}
        />
      )}
    </>
  );
};

const LineageRow: React.FC<{
  title: string;
  items: { id: string; type: string }[];
  onOpen: (assetId: string) => void;
}> = ({ title, items, onOpen }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="overline" color="text.secondary">
      {title}
    </Typography>
    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 0.5 }}>
      {items.map(item => (
        <Tooltip key={item.id} title={humanizeRelationship(item.type)}>
          <Box
            component="img"
            src={assetFileUrl(item.id)}
            alt={item.type}
            loading="lazy"
            onClick={() => onOpen(item.id)}
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              objectFit: 'cover',
              borderRadius: 1,
              cursor: 'pointer',
              border: theme => `1px solid ${theme.palette.divider}`,
              '&:hover': { borderColor: 'primary.main' },
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  </Box>
);

/**
 * "Add to Collection" button → menu of the user's collections + an inline
 * "New collection" path. Picking a collection adds the current asset to it.
 */
const AddToCollectionButton: React.FC<{
  asset: AssetSummary;
  onToast: (t: Toast) => void;
}> = ({ asset, onToast }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const open = Boolean(anchorEl);

  const { data: collections, isLoading } = useCollections();
  const addAssets = useAddAssetsToCollection();
  const createCollection = useCreateCollection();

  const close = () => {
    setAnchorEl(null);
    setCreating(false);
    setNewName('');
  };

  const addTo = (collectionId: string, name: string) => {
    addAssets.mutate(
      { id: collectionId, body: { assetIds: [asset.assetId] } },
      {
        onSuccess: () => onToast({ msg: `Added to “${name}”`, severity: 'success' }),
        onError: () => onToast({ msg: 'Failed to add to collection', severity: 'error' }),
      },
    );
    close();
  };

  const createAndAdd = () => {
    const name = newName.trim();
    if (!name) return;
    // image-lab assets carry studioId; default the collection's lab to image-lab.
    createCollection.mutate(
      {
        labId: 'image-lab',
        studioId: asset.studioId,
        name,
        initialAssetIds: [asset.assetId],
      },
      {
        onSuccess: () => onToast({ msg: `Created “${name}” with this asset`, severity: 'success' }),
        onError: () => onToast({ msg: 'Failed to create collection', severity: 'error' }),
      },
    );
    close();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CollectionsBookmarkIcon />}
        onClick={e => setAnchorEl(e.currentTarget)}
        fullWidth
      >
        Add to collection
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        {isLoading && <MenuItem disabled>Loading…</MenuItem>}
        {!isLoading &&
          (collections ?? []).map(c => (
            <MenuItem key={c.id} onClick={() => addTo(c.id, c.name)}>
              <ListItemText primary={c.name} secondary={`${c.assetCount} assets`} />
            </MenuItem>
          ))}
        {!isLoading && (collections ?? []).length === 0 && !creating && (
          <MenuItem disabled>No collections yet</MenuItem>
        )}
        <Divider />
        {creating ? (
          <Box sx={{ px: 2, py: 1, width: 260 }} onKeyDown={e => e.stopPropagation()}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Collection name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') createAndAdd();
              }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="flex-end">
              <Button size="small" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={createAndAdd}
                disabled={!newName.trim() || createCollection.isPending}
              >
                Create
              </Button>
            </Stack>
          </Box>
        ) : (
          <MenuItem onClick={() => setCreating(true)}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            New collection
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const navButtonSx = (side: 'left' | 'right') => ({
  position: 'absolute' as const,
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 12,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: 'common.white',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
});
